import { Button, Checkbox, Group, Modal, Stack, Text } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { IconUser } from '@tabler/icons-react';
import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import LocalMemberSelector from '@/components/Access/LocalMemberSelector';
import { batchTransferRobots } from 'packages/app/apis/services/robots';
import { getUsersBots, type UserBotsItem } from '@mygpt/app/robots/list';
import { useAdminStore } from '@/store/admin';
import { usePermissionData } from '@/pages/NewAdminConsole/hooks/usePermissionData';

interface MemberData {
  id: string | number;
  uuid?: string;
  name: string;
  email: string;
  enabled?: boolean;
  userCompanyEnabled?: boolean;
  departmentId?: string;
}

interface BatchDeleteModalProps {
  opened: boolean;
  onClose: () => void;
  selectedMembers: MemberData[];
  onSuccess: () => void;
}

export const BatchDeleteModal = ({ opened, onClose, selectedMembers, onSuccess }: BatchDeleteModalProps) => {
  const { t } = useTranslation();
  const { departmentTree = [] } = useAdminStore();

  const [targetUser, setTargetUser] = useState<any>(null);
  const [clearCollaborators, setClearCollaborators] = useState(true);
  const [forceTransferDatasets, setForceTransferDatasets] = useState(true);
  const [loading, setLoading] = useState(false);
  const [userSelectorOpened, setUserSelectorOpened] = useState(false);
  const [userBotsList, setUserBotsList] = useState<UserBotsItem[]>([]);
  const [loadingBots, setLoadingBots] = useState(false);
  const { batchDeleteUsers } = usePermissionData();

  // 获取待删除用户部门的管理者或企业管理者的ID
  const defaultSelectedUserIds = useMemo<string[]>(() => {
    // 从待删除用户中获取第一个有部门的用户的 departmentId
    const targetDepartmentId = selectedMembers.find((m) => m.departmentId)?.departmentId;
    console.log('targetDepartmentId2', targetDepartmentId, selectedMembers);
    if (!targetDepartmentId) return [];

    // 递归获取所有用户
    const getAllUsers = (tree: any[], users: any[] = []): any[] => {
      tree.forEach((dept) => {
        if (dept.members) {
          users.push(...dept.members);
        }
        if (dept.children?.length > 0) {
          getAllUsers(dept.children, users);
        }
      });
      return users;
    };

    const allUsers = [...getAllUsers(departmentTree)];

    console.log('allUsers', allUsers, departmentTree);

    // 1. 首先查找待删除用户所在部门的管理者（ROLE_ADMIN_DEPARTMENT 且 departmentId 匹配）
    const deptAdmin = allUsers.find(
      (user) =>
        user.departmentId === targetDepartmentId &&
        user.roles?.some((role: any) => role.code === 'ROLE_ADMIN_DEPARTMENT')
    );

    if (deptAdmin) {
      return [String(deptAdmin.id)];
    }

    // 2. 如果没有找到部门管理者，查找企业管理者（ROLE_ADMIN 或 ROLE_COMPANY_OWNER）
    const companyAdmin = allUsers.find((user) =>
      user.roles?.some((role: any) => role.code === 'ROLE_ADMIN' || role.code === 'ROLE_COMPANY_OWNER')
    );

    if (companyAdmin) {
      return [String(companyAdmin.id)];
    }

    return [];
  }, [selectedMembers, departmentTree]);

  // 获取待删除用户的机器人列表
  useEffect(() => {
    if (opened && selectedMembers.length > 0) {
      const fetchUserBots = async () => {
        setLoadingBots(true);
        try {
          const userIds = selectedMembers
            .map((m) => {
              const uuid = m.uuid;
              if (!uuid) return null;
              return uuid.includes('|') ? uuid : `gptbase|${uuid}`;
            })
            .filter((id): id is string => id !== null);

          const res = await getUsersBots(userIds);
          setUserBotsList(res.data || []);
        } catch (error) {
          console.error('Failed to fetch user bots:', error);
          setUserBotsList([]);
        } finally {
          setLoadingBots(false);
        }
      };
      fetchUserBots();
    } else {
      setUserBotsList([]);
    }
  }, [opened, selectedMembers]);

  // 计算总机器人数量，判断是否有机器人
  const totalBotsCount = userBotsList.reduce((sum, item) => sum + item.bots.length, 0);
  const hasBots = totalBotsCount > 0;

  // 收集所有机器人ID
  const getAllBotIds = (): string[] => {
    const botIds: string[] = [];
    userBotsList.forEach((item) => {
      botIds.push(...item.bots);
    });
    return botIds;
  };

  const handleUserSelect = (users: any) => {
    if (users.length > 0) {
      setTargetUser(users[0]);
      setUserSelectorOpened(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      const userIds = selectedMembers.map((m) => String(m.id));

      // 如果有机器人且选择了目标用户，先转移机器人
      if (hasBots && targetUser) {
        // 检查目标用户是否是待删除用户之一
        const isTargetUserToDelete = selectedMembers.some(
          (m) => String(m.id) === String(targetUser.id) || m.email === targetUser.email
        );

        if (isTargetUserToDelete) {
          showNotification({
            title: t('adminConsole.newConsole.batchDelete.invalid_target_title'),
            message: t('adminConsole.newConsole.batchDelete.invalid_target_message'),
            color: 'red',
          });
          setLoading(false);
          return;
        }

        const botIds = getAllBotIds();
        if (botIds.length > 0) {
          await batchTransferRobots({
            bot_ids: botIds,
            email: targetUser.email,
            clear_collaborators: clearCollaborators,
            force_transfer_dataset: forceTransferDatasets,
          });

          showNotification({
            message: t('access.transfer.success'),
            color: 'green',
          });
        }
      }

      // 批量删除用户
      await batchDeleteUsers(userIds);

      handleClose();
      onSuccess();
    } catch (error: any) {
      console.error('Batch delete error:', error?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setTargetUser(null);
    setClearCollaborators(true);
    setForceTransferDatasets(true);
    setLoading(false);
    setUserBotsList([]);
    onClose();
  };

  return (
    <>
      <Modal
        opened={opened}
        onClose={handleClose}
        title={t('adminConsole.tip.batch_delete_user')}
        centered
        withCloseButton
      >
        <Stack gap="md">
          {loadingBots && (
            <Text c="dimmed" size="sm" ta="center">
              {t('adminConsole.newConsole.batchDelete.detecting_assets')}
            </Text>
          )}
          {!loadingBots && !hasBots && (
            <Text c="dimmed" size="sm" ta="center">
              {t('adminConsole.newConsole.batchDelete.no_assets')}
            </Text>
          )}

          {/* 只有当有机器人时才显示资产转让相关表单 */}
          {hasBots && !loadingBots && (
            <>
              {/* 机器人总数提示 */}
              <Text size="sm">
                {t('adminConsole.newConsole.batchDelete.bots_will_be_deleted', { count: totalBotsCount })}
              </Text>

              {/* 目标用户选择 */}
              <Group>
                <Text fw={500}>{t('adminConsole.newConsole.batchDelete.data_recipient')}</Text>
                {targetUser ? (
                  <Group>
                    <Text>{targetUser.email}</Text>
                    <Button variant="light" size="xs" onClick={() => setTargetUser(null)}>
                      {t('access.transfer.select_user_button')}
                    </Button>
                  </Group>
                ) : (
                  <Button
                    variant="light"
                    size="xs"
                    onClick={() => setUserSelectorOpened(true)}
                    leftSection={<IconUser size={14} />}
                  >
                    {t('access.transfer.select_user_button')}
                  </Button>
                )}
              </Group>

              <>
                <Checkbox
                  label={t('access.transfer.clear_collaborators')}
                  description={t('access.transfer.clear_collaborators_desc')}
                  checked={clearCollaborators}
                  onChange={(e) => setClearCollaborators(e.currentTarget.checked)}
                  color="violet"
                />

                <Checkbox
                  label={t('access.transfer.force_transfer_datasets')}
                  checked={forceTransferDatasets}
                  onChange={(e) => setForceTransferDatasets(e.currentTarget.checked)}
                  color="violet"
                />
              </>
            </>
          )}

          {/* 确认按钮 */}
          <Group justify="flex-end" mt={8}>
            <Button variant="outline" onClick={handleClose}>
              {t('access.transfer.cancel')}
            </Button>
            <Button onClick={handleDelete} loading={loading} color="red" disabled={hasBots && !targetUser}>
              {t('delete_btn')}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* 用户选择器 */}
      {userSelectorOpened && (
        <LocalMemberSelector
          key="batch-delete-user-selector"
          open={userSelectorOpened}
          onClose={() => setUserSelectorOpened(false)}
          onConfirm={handleUserSelect}
          maxSelect={1}
          // 有bug，先不放开
          // defaultSelectedUserIds={defaultSelectedUserIds}
        />
      )}
    </>
  );
};
